import dayjs from "dayjs"
import type { StoreTimeOption } from "../types/store.types"
import type { StoreItem } from "../hooks/useStore"

export default Object.freeze({
  formatStore: (store: StoreItem) => {
    // Return the formatted store 
    const openTime = store?.openTimeDisplay || '08:00'
    const closeTime = store?.closeTimeDisplay || '22:00'
    const feature_image = store?.imageDisplay || `https://placehold.co/256x256?text=${store?.name}`

    return {
      id: store?.id,
      storeNo: store?.storeNo,
      name: store?.name,
      address: store?.address,
      feature_image,
      phoneNumber: store?.phoneNumber,
      distance: store?.distanceDisplay,
      opening_hours: generateTimeOptions(openTime, closeTime, parseInt(store?.original_data?.addMins || 30)),
      open_time: openTime,
      close_time: closeTime,
      status: checkStatus(store),
      location: {
        lat: store?.latitude,
        lng: store?.longitude,
      },
      menu_code: store?.defaultMenuCode,
      original_data: store,
    }
  },
  generateTimeOptions: generateTimeOptions,
})

/**
 * Kiểm tra trạng thái của cửa hàng
 * @param store 
  * @returns {Object} Trạng thái cửa hàng
  * @property {string} key - Trạng thái cửa hàng
  * @property {string} label - Nhãn trạng thái cửa hàng
  * @property {string} reason - Lý do cửa hàng
 */

export enum StoreStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  CLOSED_BY_TIME = "closed_by_time",
  IN_ACTIVE = 'in_active',
  TEMPORARILY_CLOSED = 'temporarily_closed',
}

export enum StoreOpenStatus {
  OPEN = 'Open',
  OFF = 'Off'
}

const checkStatus = (store: StoreItem['original_data']) => {
  /**
   *  Trường hợp cửa hàng đang kích hoạt và 
   *  Trả về trạng thái "Đóng cửa"
   *  với lý do "Cửa hàng không có thông tin"
   */
  if (!store?.isActive && store?.isOpen === StoreOpenStatus.OPEN) {
    return {
      key: StoreStatus.OPEN,
      label: 'Đang hoạt động',
      reason: 'Cửa hàng đang hoạt động',
    }
  }

  if (!store?.isActive && store?.isOpen === StoreOpenStatus.OFF) {
    
    /**
     * Trường hợp cửa hàng tắt thời gian trên admin
     * closeTimeDisplay = null
     */
    if (!store?.closeTimeDisplay) {
      return {
        key: StoreStatus.CLOSED_BY_TIME,
        label: 'Ngoài giờ phục vụ',
        reason: 'Nhà hàng sẽ mở cửa lại vào lúc 07:00 AM <br /> Quý khách có thể chọn món và đặt trước cho ngày mai.',
      }
    }

    return {
      key: StoreStatus.CLOSED,
      label: 'Tạm đóng',
      reason: 'Cửa hàng tạm đóng cửa',
    }
  }
  /**
   * Trường hợp admin không kích hoạt cửa hàng
   * Trả về trạng thái "Ngừng hoạt động"
   * với lý do "Cửa hàng đang ngừng kích hoạt"
   */
  if (!store?.isActive) {
    return {
      key: StoreStatus.IN_ACTIVE,
      label: 'Ngừng hoạt động',
      reason: 'Cửa hàng đang ngừng kích hoạt',
    }
  }

  return {
    key: StoreStatus.OPEN,
    label: 'Đang hoạt động',
    reason: 'Cửa hàng đang hoạt động',
  }
}

export function generateTimeOptions(
  openTimeStr: string,
  closeTimeStr: string,
  stepMinutes = 30
): StoreTimeOption[] {
  const format = 'HH:mm'
  const todayStr = dayjs().format('YYYY-MM-DD')

  const openTime = dayjs(`${todayStr} ${openTimeStr}`)
  const closeTime = dayjs(`${todayStr} ${closeTimeStr}`)
  const currentTime = dayjs()

  const diffMinutes = currentTime.diff(openTime, 'minute')
  const stepOffset = Math.max(0, Math.ceil(diffMinutes / stepMinutes))

  let pointer = openTime.add(stepOffset * stepMinutes, 'minute')

  const result: StoreTimeOption[] = []

  while (true) {
    const endTime = pointer.add(stepMinutes, 'minute')

    if (endTime.isAfter(closeTime)) {
      break
    }

    result.push({
      value: pointer.format(),
      name: `${pointer.format(format)} - ${endTime.format(format)}`
    })

    pointer = endTime
  }

  return result
}
